import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMe } from '@/api/domains/merchant-settings';
import { useCompleteOnboarding } from '@/api/domains/onboarding';
import type { MerchantInfo } from '@/api/types/settings';

import { SetupLayout } from './SetupLayout';
import { ConnectInPostStep } from './steps/ConnectInPostStep';
import { ConnectShopifyStep } from './steps/ConnectShopifyStep';
import { SelectMarketStep } from './steps/SelectMarketStep';

type Step = 'shopify' | 'inpost' | 'market';

function initialStep(merchant: MerchantInfo): Step {
  // Step 1 (Connect Shopify) only matters when the store isn't linked yet.
  // `installed_at` is set at install, so any merchant reaching the wizard from
  // Shopify (embedded or standalone) is already connected and skips to InPost.
  if (merchant.installed_at === null) return 'shopify';
  if (!merchant.has_inpost_connection) return 'inpost';
  return 'market';
}

export function SetupWizardPage() {
  const meQuery = useMe();
  const navigate = useNavigate();
  const completeOnboarding = useCompleteOnboarding();
  const [step, setStep] = useState<Step | null>(null);

  useEffect(() => {
    if (step === null && meQuery.data) {
      setStep(initialStep(meQuery.data.merchant));
    }
  }, [step, meQuery.data]);

  // Skip dismisses the wizard for good (marks it complete) and drops the
  // merchant on their dashboard.
  const handleSkip = () =>
    completeOnboarding.mutate(undefined, {
      onSuccess: () => navigate('/shipments'),
      onError: () => navigate('/shipments'),
    });

  if (step === null) {
    return (
      <SetupLayout>
        <div className="text-muted-foreground">…</div>
      </SetupLayout>
    );
  }

  return (
    <SetupLayout onSkip={handleSkip} skipPending={completeOnboarding.isPending}>
      {step === 'shopify' ? <ConnectShopifyStep /> : null}
      {step === 'inpost' ? <ConnectInPostStep onContinue={() => setStep('market')} /> : null}
      {step === 'market' ? <SelectMarketStep /> : null}
    </SetupLayout>
  );
}
